import { ChevronDown, ChevronUp, Plus, Save, Trash } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import { useIllustrationsApi } from 'src/api/db/apiProductImages'
import { useProductVariantColorApi } from 'src/api/db/apiProductVariantColor'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Card, Image, Input } from 'src/shared/components/ui'
import { CardContent } from 'src/shared/components/ui/card'
import { Button } from 'src/shared/components/ui/button'
import { ScrollArea } from 'src/shared/components/ui/scroll-area'
import { ColorItemProps } from '../types'
import { useIllustrationImageUpload } from '../hooks'
import { buildOrders, parseImageUrls } from '../utils'
import { ImageItem } from './ImageItem'
import { ImageUploadSection } from './shared/ImageUploadSection'
import { SCROLL_HEIGHTS } from '../consts'

export const ColorItem = ({
  color,
  illustration,
  id,
  onColorItemChange,
}: ColorItemProps) => {
  const [color_, setColor_] = useState(color)
  const [originalColor, setOriginalColor] = useState(color)
  const [newImageUrls, setNewImageUrls] = useState<string>('')
  const [openAccordion, setOpenAccordion] = useState<string | undefined>(
    undefined
  )
  const { updateIllustrationsBulk, deleteIllustration, createIllustration } =
    useIllustrationsApi({})
  const { updateProductVariantColor, deleteProductVariantColor } =
    useProductVariantColorApi()

  const { handleImageUpload, isUploading, error } = useIllustrationImageUpload(
    newImageUrls,
    setNewImageUrls
  )

  const [currentOrders, setCurrentOrders] = useState<Record<number, number>>(
    () => buildOrders(illustration)
  )
  const [originalOrders, setOriginalOrders] = useState<Record<number, number>>(
    () => buildOrders(illustration)
  )

  const hasOrderChanges = Object.keys(currentOrders || {}).some((key) => {
    const k = Number(key)
    return currentOrders[k] !== originalOrders[k]
  })

  const hasColorNameChange = color_ !== originalColor
  const hasNewImages = !!newImageUrls

  useEffect(() => {
    const hasChanges = hasOrderChanges || hasColorNameChange || hasNewImages
    onColorItemChange(id, hasChanges)
  }, [hasOrderChanges, hasColorNameChange, hasNewImages, id, onColorItemChange])

  useEffect(() => {
    if (color !== originalColor && color_ === originalColor) {
      setColor_(color)
      setOriginalColor(color)
    }
  }, [color, originalColor, color_])

  useEffect(() => {
    const next = buildOrders(illustration)
    const changed = Object.keys(next).some(
      (key) => next[Number(key)] !== originalOrders[Number(key)]
    )
    if (changed) {
      setCurrentOrders(next)
      setOriginalOrders(next)
    }
  }, [illustration, originalOrders])

  const handleAddImages = async () => {
    if (!newImageUrls) return

    const urls = parseImageUrls(newImageUrls)
    await Promise.all(
      urls.map((url) =>
        createIllustration.mutateAsync({
          product_variant_color_id: id,
          image_url: url.trim(),
        })
      )
    )
    setNewImageUrls('')
  }

  const handleOrderChange = (itemId: number, order: number) => {
    setCurrentOrders((prev) => ({ ...prev, [itemId]: order }))
  }

  const handleDelete = (itemId: number) => {
    deleteIllustration.mutate(itemId)
  }

  const handleSaveAll = () => {
    const rows = Object.keys(currentOrders || {})
      .map((key) => Number(key))
      .filter((itemId) => currentOrders[itemId] !== originalOrders[itemId])
      .map((itemId) => ({ id: itemId, order: currentOrders[itemId] })) as any

    if (rows.length === 0) return

    updateIllustrationsBulk.mutate(rows, {
      onSuccess: () => {
        setOriginalOrders((prev) => ({ ...prev, ...currentOrders }))
      },
    })
  }

  const isOpen = openAccordion === id.toString()
  const hasChanges = hasOrderChanges || hasColorNameChange || hasNewImages

  // Sort illustrations by order for display
  const sortedIllustrations = useMemo(() => {
    if (!illustration || illustration.length === 0) return []
    return [...illustration].sort((a, b) => {
      const orderA = currentOrders[a.id] ?? a.order ?? 0
      const orderB = currentOrders[b.id] ?? b.order ?? 0
      return orderA - orderB
    })
  }, [illustration, currentOrders])

  // Get first few images for thumbnail preview
  const thumbnailImages = sortedIllustrations.slice(0, 4)

  return (
    <Card className={hasChanges ? 'ring-2 ring-primary/20' : ''}>
      <Column className="gap-3">
        <Row className="items-center justify-between gap-4">
          <Row className="items-center gap-3 flex-1">
            <Input
              value={color_}
              placeholder="Color name"
              onChange={(e) => {
                setColor_(e.target.value)
              }}
              className="max-w-[200px]"
            />

            {hasColorNameChange && (
              <Button
                size="sm"
                onClick={() => {
                  updateProductVariantColor.mutate(
                    {
                      id,
                      color: color_,
                    },
                    {
                      onSuccess: () => {
                        setOriginalColor(color_)
                      },
                    }
                  )
                }}
                disabled={updateProductVariantColor.isPending}
              >
                <Save className="h-3 w-3 mr-1" />
                Save name
              </Button>
            )}

            {/* Image thumbnails preview when collapsed */}
            {!isOpen && thumbnailImages.length > 0 && (
              <Row className="items-center gap-2 ml-2">
                <span className="text-xs text-muted-foreground">
                  {illustration.length} image
                  {illustration.length !== 1 ? 's' : ''}
                </span>
                <Row className="gap-1">
                  {thumbnailImages.map(({ image_url, id: imgId }) => (
                    <div
                      key={imgId}
                      className="relative w-10 h-10 rounded border border-border overflow-hidden bg-muted"
                    >
                      <Image
                        src={image_url || ''}
                        alt=""
                        width={40}
                        height={40}
                        className="object-cover w-full h-full"
                      />
                    </div>
                  ))}
                  {illustration.length > 4 && (
                    <div className="w-10 h-10 rounded border border-border bg-muted flex items-center justify-center text-xs text-muted-foreground">
                      +{illustration.length - 4}
                    </div>
                  )}
                </Row>
              </Row>
            )}
          </Row>

          <Row className="items-center gap-2">
            {hasOrderChanges && (
              <Button
                size="sm"
                disabled={updateIllustrationsBulk.isPending}
                onClick={handleSaveAll}
                variant="secondary"
              >
                <Save className="h-3 w-3 mr-1" />
                Save order
              </Button>
            )}

            <Button
              size="sm"
              variant="destructive"
              onClick={() => {
                deleteProductVariantColor.mutate(id)
              }}
              disabled={deleteProductVariantColor.isPending}
            >
              <Trash className="h-3 w-3 mr-1" />
              Delete
            </Button>

            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setOpenAccordion(isOpen ? undefined : id.toString())
              }}
            >
              {isOpen ? (
                <>
                  <ChevronUp className="h-4 w-4 mr-1" />
                  Collapse
                </>
              ) : (
                <>
                  <ChevronDown className="h-4 w-4 mr-1" />
                  Expand
                </>
              )}
            </Button>
          </Row>
        </Row>
      </Column>

      {isOpen && (
        <CardContent>
          <Column className="gap-6">
            {/* Existing Images Section - Show FIRST */}
            {sortedIllustrations.length > 0 ? (
              <Column className="gap-3">
                <Row className="items-center justify-between">
                  {hasOrderChanges && (
                    <p className="text-xs text-muted-foreground">
                      Reorder images and click "Save order" above
                    </p>
                  )}
                </Row>
                <ScrollArea className="max-h-[400px]">
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 p-1">
                    {sortedIllustrations.map(
                      ({ image_url, id: imgId, order }) => {
                        return (
                          <ImageItem
                            key={imgId}
                            image_url={image_url || ''}
                            id={imgId}
                            order={currentOrders[imgId] ?? order ?? 0}
                            onOrderChange={handleOrderChange}
                            onDelete={handleDelete}
                          />
                        )
                      }
                    )}
                  </div>
                </ScrollArea>
              </Column>
            ) : (
              <div className="text-center py-8 text-muted-foreground text-sm">
                No images yet. Add images below.
              </div>
            )}

            {/* Add New Images Section - Show SECOND */}
            <Card className="bg-muted/20">
              <Row className="items-center gap-2 mb-3">
                <Plus className="h-3 w-3" />
                <p className="text-sm font-medium">New images</p>
              </Row>

              <Column className="gap-3">
                <ImageUploadSection
                  imageUrls={newImageUrls}
                  setImageUrls={setNewImageUrls}
                  handleImageUpload={handleImageUpload}
                  isUploading={isUploading}
                  error={error}
                  fileInputId={`file-upload-${id}`}
                  label="Image URLs"
                  placeholder=""
                  scrollHeight={SCROLL_HEIGHTS.COLOR_ITEM_NEW_IMAGES}
                  showPreview={true}
                  imageSize={{ width: 100, height: 100 }}
                />

                <Row className="justify-end">
                  <Button
                    type="button"
                    variant="primary"
                    size="sm"
                    onClick={handleAddImages}
                    disabled={
                      !newImageUrls.trim() || createIllustration.isPending
                    }
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    {createIllustration.isPending ? 'Adding...' : 'Add images'}
                  </Button>
                </Row>
              </Column>
            </Card>
          </Column>
        </CardContent>
      )}
    </Card>
  )
}
